import React, { useEffect, useState } from "react";
import client, { formatApiError } from "@/lib/api";
import {
  Search, RotateCcw, FastForward, Rewind, Zap, Eraser, Loader2,
  Mail, Phone, Globe2, BookOpen, ShieldAlert, SlidersHorizontal,
} from "lucide-react";
import AdminPageHeader from "@/components/admin/AdminPageHeader";

function ActionButton({ icon: Icon, pending, disabled, onClick, children, tone }) {
  const toneCls = tone === "danger" ? "text-hv-coral" : "text-hv-navy";
  return (
    <button onClick={onClick} disabled={disabled || pending}
            className={`inline-flex items-center gap-1 rounded-full bg-white border border-[#E5DFC8] px-3 py-1.5 text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed ${toneCls} hover:border-hv-teal`}>
      {pending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" strokeWidth={2.5} />}
      {children}
    </button>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-wide text-hv-slate mb-1">{label}</p>
      <div className="rounded-xl bg-hv-cream px-3 py-2 flex items-center gap-2">
        <Icon className="w-3.5 h-3.5 text-hv-slate shrink-0" />
        <p className="text-sm font-semibold truncate text-hv-navy">{value || "—"}</p>
      </div>
    </div>
  );
}

export default function AdminLearnerOverrides() {
  const [q, setQ] = useState("");
  const [learners, setLearners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pending, setPending] = useState("");
  const [days, setDays] = useState(3);
  const [forceSeq, setForceSeq] = useState(10);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      const { data } = await client.get(`/admin/release/learners?${params.toString()}`);
      setLearners(data.learners);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]); // eslint-disable-line

  const loadDetail = async (id) => {
    setSelectedId(id);
    setDetailLoading(true);
    try {
      const { data } = await client.get(`/admin/release/learners/${id}`);
      setDetail(data);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const act = async (action, body) => {
    if (!selectedId) return;
    setErr(""); setMsg(""); setPending(action);
    try {
      await client.post(`/admin/release/learners/${selectedId}/${action}`, body || {});
      setMsg("Updated.");
      await loadDetail(selectedId);
      load();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setPending("");
    }
  };

  const resetProgress = async () => {
    if (!window.confirm("Reset ALL progress for this learner?")) return;
    await act("reset-progress", {});
  };

  return (
    <div data-testid="admin-overrides">
      <AdminPageHeader icon={SlidersHorizontal} title="Learner Overrides" subtitle="Search a learner, then adjust their release clock" />

      <section className="rounded-3xl bg-white border border-[#E5DFC8] p-5">
        <form className="flex gap-2 mb-4" onSubmit={(e) => e.preventDefault()}>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute start-4 top-1/2 -translate-y-1/2 text-hv-slate" size={16} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search learner by name or email…"
                   data-testid="overrides-search"
                   className="w-full rounded-full border-2 border-[#E5DFC8] focus:border-hv-teal bg-white ps-11 pe-4 py-2.5 font-semibold outline-none" />
          </div>
        </form>

        {err && <div className="mb-4 text-hv-coral font-bold text-sm" data-testid="overrides-error">{err}</div>}
        {msg && <div className="mb-4 text-hv-teal font-bold text-sm" data-testid="overrides-msg">{msg}</div>}

        <div className="grid lg:grid-cols-[300px_1fr] gap-5 items-start">
          <ul className="space-y-1.5 max-h-[600px] overflow-y-auto pr-1 lg:sticky lg:top-8" data-testid="overrides-list">
            {loading ? (
              <p className="text-hv-slate text-sm px-2">Loading…</p>
            ) : learners.length === 0 ? (
              <p className="text-hv-slate text-sm px-2">No learners found.</p>
            ) : learners.map((l) => {
              const isSelected = l.id === selectedId;
              return (
                <li key={l.id}>
                  <button onClick={() => loadDetail(l.id)} data-testid={`overrides-select-${l.id}`}
                          className={`w-full flex items-center gap-3 text-left rounded-2xl px-3.5 py-2.5 border-2 transition-colors ${
                            isSelected ? "bg-hv-tealTint border-hv-teal" : "bg-white border-[#E5DFC8] hover:border-hv-teal hover:bg-hv-cream"}`}>
                    <div className="w-8 h-8 rounded-full bg-hv-navy text-white flex items-center justify-center text-xs font-bold shrink-0">
                      {(l.name || l.email || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-hv-navy truncate">{l.name || l.email}</p>
                      <p className="text-xs text-hv-slate truncate">Unlocked to #{l.max_unlocked} · {l.completed} done</p>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>

          {!selectedId ? (
            <div className="h-full min-h-[240px] rounded-2xl border-2 border-dashed border-[#E5DFC8] flex items-center justify-center text-center px-6">
              <p className="text-hv-slate text-sm">Select a learner to view and adjust their release schedule.</p>
            </div>
          ) : detailLoading || !detail ? (
            <p className="text-hv-slate text-sm">Loading…</p>
          ) : (
            <div className="space-y-4">
              <div className="rounded-2xl border border-[#E5DFC8] p-4 grid sm:grid-cols-2 gap-3">
                <InfoRow icon={Mail} label="Email" value={detail.learner.email} />
                <InfoRow icon={Phone} label="Parent contact" value={detail.learner.parent_contact} />
                <InfoRow icon={Globe2} label="Language" value={detail.learner.language} />
                <InfoRow icon={BookOpen} label="Completed" value={`${detail.completed_count} stories`} />
              </div>

              {detail.override && (
                <div className="rounded-2xl bg-hv-coralTint px-4 py-3 flex items-center gap-2 text-sm font-semibold text-hv-navy">
                  <ShieldAlert className="w-4 h-4 text-hv-coral shrink-0" />
                  Active override: {detail.override.type} ({String(detail.override.value)})
                </div>
              )}

              <div className="rounded-2xl border border-[#E5DFC8] p-4 flex flex-wrap items-center gap-2">
                <ActionButton icon={RotateCcw} pending={pending === "reset-anchor"} onClick={() => act("reset-anchor")}>
                  Reset anchor to today
                </ActionButton>
                <input type="number" min="1" value={days} onChange={(e) => setDays(e.target.value)}
                       className="w-16 px-2 py-1.5 rounded-full border border-[#E5DFC8] text-xs" />
                <ActionButton icon={FastForward} pending={pending === "skip-ahead"} onClick={() => act("skip-ahead", { days: Number(days) })}>
                  Skip ahead
                </ActionButton>
                <ActionButton icon={Rewind} pending={pending === "delay-release"} onClick={() => act("delay-release", { days: Number(days) })}>
                  Delay
                </ActionButton>
                <input type="number" min="1" max="600" value={forceSeq} onChange={(e) => setForceSeq(e.target.value)}
                       className="w-16 px-2 py-1.5 rounded-full border border-[#E5DFC8] text-xs" />
                <ActionButton icon={Zap} pending={pending === "force-release"} onClick={() => act("force-release", { seq: Number(forceSeq) })}>
                  Force unlock to #{forceSeq}
                </ActionButton>
                <ActionButton icon={Eraser} tone="danger" pending={pending === "reset-progress"} onClick={resetProgress}>
                  Reset all progress
                </ActionButton>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
